/**
 * pulse_diag/v1 envelope (OBS-VALIDATION-01).
 * Decimal strings for 64-bit-ish counters that may exceed Number precision.
 */

export const SCHEMA_VERSION = 'pulse_diag/v1';

export type DiagMode = 'off' | 'summary' | 'trace';

export function wallUtcNow(): string {
  return new Date().toISOString();
}

export function monoNs(): string {
  // performance.now is ms; expose as ns-scale decimal string for schema parity.
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return String(Math.round(performance.now() * 1e6));
  }
  return String(Date.now() * 1e6);
}

export function newSourceInstanceId(prefix: string): string {
  const rand =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().slice(0, 12)
      : Math.random().toString(16).slice(2, 14);
  return `${prefix}-${rand}`;
}

export interface DiagEnvelope {
  schema_version: string;
  event: string;
  event_id: string;
  source_instance_id: string;
  source_seq: number;
  session_seq: number | null;
  diagnostic_run_id: string;
  session_id: string | null;
  room_name: string | null;
  component: string;
  build_ref: string;
  observed_wall_utc: string;
  observed_mono_ns: string;
  clock_domain_id: string;
  parent_event_id?: string | null;
  operation_id?: string | null;
  mode: DiagMode;
  data: Record<string, unknown>;
  unavailable: Record<string, string>;
  provenance: string;
}

export function makeEnvelope(args: {
  event: string;
  source_instance_id: string;
  source_seq: number;
  session_seq: number | null;
  diagnostic_run_id: string;
  session_id: string | null;
  room_name: string | null;
  component: string;
  build_ref: string;
  mode: DiagMode;
  data?: Record<string, unknown>;
  unavailable?: Record<string, string>;
  provenance?: string;
  parent_event_id?: string | null;
  operation_id?: string | null;
}): DiagEnvelope {
  return {
    schema_version: SCHEMA_VERSION,
    event: args.event,
    event_id: `${args.source_instance_id}:${args.source_seq}`,
    source_instance_id: args.source_instance_id,
    source_seq: args.source_seq,
    session_seq: args.session_seq,
    diagnostic_run_id: args.diagnostic_run_id,
    session_id: args.session_id,
    room_name: args.room_name,
    component: args.component,
    build_ref: args.build_ref,
    observed_wall_utc: wallUtcNow(),
    observed_mono_ns: monoNs(),
    clock_domain_id: `perf:${args.source_instance_id}`,
    parent_event_id: args.parent_event_id ?? null,
    operation_id: args.operation_id ?? null,
    mode: args.mode,
    data: args.data ?? {},
    unavailable: args.unavailable ?? {},
    provenance: args.provenance ?? 'measured',
  };
}
